import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Button,
  Table,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  NavItem,
  NavLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Badge 
} from "reactstrap";
import { Avatar } from "../components/Avatar";
import { formatDistanceToNow, parseISO, format } from "date-fns";
import placeholder from "assets/img/placeholder.jpg";
import AddNewOrderModal from "./AddNewOrderModal";
import Select, { components as selectComponents } from "react-select";
import { DataContext } from "../context/DataContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getOrderDetails as fetchOrderDetails, updateCustomerRange, updateOrder } from "../context/api";
import { 
  updateCustomer, 
  createCustomer, 
  createContact, 
  createNote, 
  updateNote, 
  updateContact,
  deleteContact,
  deleteOrder,
  deleteNote  
} 
  from "../context/api";
import { useLocation } from "react-router-dom"; // Import useLocation
import { s3ImageUrl } from "../utils/s3Url";
import {
  canEditOrders,
  getOrderActivityTimestamp,
  syncOrderIntoCustomers,
} from "../utils/orderHistory";

// Initial list of customers

const normalizeProductId = (id) => {
  const numeric = Number(id);
  return Number.isInteger(numeric) ? numeric : id;
};


function CustomerDashboard() {
  const location = useLocation(); // Initialize useLocation
  const { data, setData, loading } = useContext(DataContext);
  const userProfile = data.userProfile || {};
  const canEditOrderEntries = canEditOrders(userProfile.email);
  const banners = data.banners || [];
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);
  const [editOrderModalOpen, setEditOrderModalOpen] = useState(false);
  const [orderBeingEdited, setOrderBeingEdited] = useState(null);
  const customers = data.customers || [];
  // console.log("Customers from context:", customers);
  const territories = data.territories || []; // Make sure territories is in scope
  const distributionsFromContext = data.distributions || []; // Get distributions from context
  const promotions = data.promotions || [];

  const storeTypeOptions = data.storeType || [];

  // console.log("Store Types:", data);
  const bannerOptions = (data.banners); // Use banners from context, assuming it might be an array of objects or strings

  const isSuperuser = data.userProfile?.is_superuser === true;
  const isAdmin = data.userProfile?.role === 'admin' || isSuperuser;
  // console.log("Customers:", customers);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [deleting, setDeleting] = useState(false);
  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  const products = data.products || [];
  const [notif, setNotif] = useState({ type: "", message: "" });

const getNormalizedItems = (order) => {
  if (!order) return [];
  const raw = order.items ?? order.orderItems ?? [];
  return raw.map(item => {
    const product = products.find(p => p.id === item.productId) || {};
    const name = item.name || item.itemName || product.name || "(unknown)";

    // Determine original/base price (fallback chain)
    const basePriceCents =
      item.unitPriceCents != null ? item.unitPriceCents :
      product.unitPriceCents != null ? product.unitPriceCents :
      product.priceCents != null ? product.priceCents :
      item.bespokePriceCents != null ? item.bespokePriceCents : 0;

    // Custom override (bespoke) if provided
    const customPriceCents = item.bespokePriceCents != null ? item.bespokePriceCents : null;
    const effectivePriceCents = customPriceCents != null ? customPriceCents : basePriceCents;

    const qty = item.quantity || 0;
    const totalCents =
      item.totalPriceCents != null ? item.totalPriceCents : effectivePriceCents * qty;

    return {
      ...item,
      name,
      basePrice: basePriceCents / 100,
      customPrice: customPriceCents != null ? customPriceCents / 100 : null,
      price: effectivePriceCents / 100,
      total: totalCents / 100,
      quantity: qty
    };
  });
};

  const images = require.context("assets/img", false, /\.(png|jpe?g|svg)$/);

  const getImageUrl = (url) => {
    if (!url) return placeholder; // fallback if URL is empty
    // Extract the file name from the URL (assumes URL is like "assets/img/monford.png")
    const parts = url.split("/");
    const fileName = parts[parts.length - 1];
    try {
      return images(`./${fileName}`);
    } catch (err) {
      return placeholder;
    }
  };

  const formatFullTimestamp = (isoString) => {
    try {
      const normalized = String(isoString || "").replace(" ", "T");
      const date = parseISO(normalized);
      if (isNaN(date)) {
        return isoString;
      }
      return format(date, "dd/MM/yyyy 'at' h:mm a");
    } catch {
      return isoString;
    }
  };

  
  const handleConfirmDelete = async () => {
    if (!selectedOrder) return;

    // 1️⃣ Optimistically update local state
    const updatedHistory = businessDetails.orderHistory.filter(
      o => o.orderId !== selectedOrder.orderId
    );
    const updatedBusiness = { ...businessDetails, orderHistory: updatedHistory };
    setBusinessDetails(updatedBusiness);

    const updatedCustomers = customers.map(c =>
      c.id === updatedBusiness.id ? updatedBusiness : c
    );
    setData({ ...data, customers: updatedCustomers });

    // 2️⃣ Close the modals right away
    setConfirmDeleteOpen(false);
    setOrderModalOpen(false);

    // 3️⃣ Fire off the API call
      try {
        setDeleting(true);
        // console.log("Deleting order:", selectedOrder);
        await deleteOrder(selectedOrder.orderId);
        setNotif({ type: "success", message: "Order deleted successfully." });
      } catch (err) {
        console.error("Failed to delete order:", err);
        // rollback…
        setNotif({ type: "error", message: "Could not delete order. Please try again." });
      } finally {
        setDeleting(false);
        setSelectedOrder(null);
      }
    };

  
// and cancelling the confirmation
const handleCancelDelete = () => {
  setConfirmDeleteOpen(false);
};

  const sortCustomersByName = (a, b) => {
    const aName = a.businessName;
    const bName = b.businessName;
    const aStartsWithNumber = /^\\d/.test(aName);
    const bStartsWithNumber = /^\\d/.test(bName);

    if (aStartsWithNumber && !bStartsWithNumber) {
      return -1; // Names starting with numbers come first
    }
    if (!aStartsWithNumber && bStartsWithNumber) {
      return 1;  // Names starting with letters come after
    }
    return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
  };

  // keep track of who’s selected by ID…
  // Sort customers for initial selection
  const sortedCustomers = [...customers].sort(sortCustomersByName);
  // Check if a customer ID was passed via route state
  const passedCustomerId = location.state?.selectedCustomerId;
  const initialCustomerId = passedCustomerId || (sortedCustomers.length > 0 ? sortedCustomers[0].id : null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(initialCustomerId);
  
  // Find the initially selected customer from the original unsorted list
  // or from the sorted list if that's easier, ensuring it's the same object reference
  // that will be used elsewhere (e.g., in businessDetails initialization).
  const initiallySelectedCustomer = customers.find(c => c.id === initialCustomerId) || {};

  // …and mirror that in businessDetails
  const [businessDetails, setBusinessDetails] = useState(initiallySelectedCustomer);
  const isArchived = businessDetails.archived === true;
  // console.log("Selected Customer:", selectedCustomer);
 
  const [selectedStoreType, setSelectedStoreType] = useState("");
  const [retrying, setRetrying] = useState(false);
  // NEW: for the type‑ahead text - Initialize to blank
  const [customerSearch, setCustomerSearch] = useState(""); // MODIFIED
  const [showSuggestions, setShowSuggestions] = useState(false); // Initialize to false
  const dropdownRef = useRef(null); // Ref for the dropdown list
  
  // selectedCustomer should be derived from selectedCustomerId and the original customers list
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId) || {};
  
  const customerBannerObj = banners.find(b => b.name === selectedCustomer.banner);
  // Build the S3 URL (ext is optional)
  const customerBannerUrl = customerBannerObj
    ? s3ImageUrl(
        "banners",
        customerBannerObj.id,
        customerBannerObj.ext ? `.${customerBannerObj.ext}` : ".png"  // e.g. ".png"
      )
    : placeholder;
    // console.log("Customer Banner URL:", customerBannerUrl);
  // Helper: only show promotions that are active (startDate <= today) or have no startDate
  const isPromoActive = (p) => {
    if (!p || p.archived) return false;
    if (!p.startDate) return true; // null/undefined means show
    const iso = typeof p.startDate === 'string' ? p.startDate.slice(0, 10) : p.startDate;
    const d = new Date(iso);
    if (isNaN(d)) return true; // if unparsable, do not hide
    const today = new Date();
    today.setHours(0,0,0,0);
    d.setHours(0,0,0,0);
    return d <= today;
  };

  const applicablePromotions = promotions.filter(p => {
    // Must be active and not archived; hide future-dated promos
    if (!isPromoActive(p)) return false;

    if (p.allCustomers) {
      return false;
    }

    const customerBanner = data.banners?.find(b => b.name === selectedCustomer.banner);
    const customerBannerId = customerBanner?.id;

    if (Array.isArray(p.bannerIds) && p.bannerIds.length) {
      // selectedCustomer.banner IS the banner name
      return p.bannerIds.includes(selectedCustomer.banner);
    }

    // Otherwise, if bannerIds is empty, check customerIds.
    if (p.customerIds && p.customerIds.length > 0) {
        return p.customerIds.includes(selectedCustomer.id);
    }

    return false;
  });

  // State for customer archive confirmation modal
  const [archiveCustomerConfirmModalOpen, setArchiveCustomerConfirmModalOpen] = useState(false);
const retryPengelly = async () => {
  if (!orderDetails) return;
  setRetrying(true);
  try {
    // 📦 Lookup customer + parse address
    const cust = data.customers.find(c => c.id === selectedCustomer.id);
    const addrArray = (() => {
      try {
        const a = JSON.parse(cust.address);
        return Array.isArray(a) ? a : [];
      } catch { return []; }
    })();
    // 📦 Build products payload
    const pengellyProducts = orderDetails.items.map(i => {
      const prod = data.products.find(p => p.id === i.productId) || {};
      return {
        code: prod.code || "N/A",
        orderQty: (i.quantity || 0) + (i.bonusQuantity || 0),
        skuType: prod.unitOfMeasure || "N/A",
        name: i.name
      };
    });
    // 📦 Find warehouse/ distribution
    const distObj = data.distributions.find(d => d.name === orderDetails.distribution) || {};
    // 📦 Assemble Pengelly payload
    const payload = {
      apiKey: process.env.REACT_APP_PENGELLY_API_KEY,
      transType: "StockOut",
      orderDate: orderDetails.date,
      warehouseID: distObj.id,
      orderNo: orderDetails.purchaseOrder || "",
      supplierOrderNo: orderDetails.orderId,
      deliverTo: {
        customerEmail: cust.contactEmail,
        customerName: cust.businessName,
        address1: addrArray[0]||"",
        address2: addrArray[1]||"",
        city:     addrArray[2]||"",
        postCode: addrArray[3]||"",
        customerPhone: cust.phone||""
      },
      products: pengellyProducts
    };
    console.log("Retrying Pengelly with payload:", payload);
    // 🔁 Call the API
    const res = await fetch(
      "https://www.pengellys.co.nz/Apps/bewebmvc/api/SubmitOrder",
      { method:"POST", headers:{ "Content-Type":"application/json" }, body: JSON.stringify(payload) }
    );
    const json = await res.json();
    console.log("Pengelly response:", json);
    if (!res.ok || !json.success || !json.pickNo) {
      throw new Error(json.message || "Unknown Pengelly error");
    }
    // ✅ On success, update internal order
    await updateOrder(orderDetails.orderId, { pick: json.pickNo });
    // Update the modal’s pick
    setOrderDetails(prev => ({ ...prev, pick: json.pickNo }));
    
    // Also update your context so the table row updates
    setData(d => ({
      ...d,
      customers: d.customers.map(cust =>
        cust.id === selectedCustomer.id
          ? {
              ...cust,
              orderHistory: cust.orderHistory.map(o =>
                o.orderId === orderDetails.orderId
                  ? { ...o, pick: json.pickNo }
                  : o
              )
            }
          : cust
      )
    }));
    setNotif({ type: "success", message: `Retry succeeded! Pick No: ${json.pickNo}` });
  } catch (err) {
    console.error("Retry failed:", err);
    setNotif({ type: "error", message: `Retry failed: ${err.message}` });
  } finally {
    setRetrying(false);
  }
};

  // Whenever we switch customers, update businessDetails _and_ the search text
  useEffect(() => {
    const customerToDisplay = customers.find(c => c.id === selectedCustomerId);

    if (customerToDisplay) {
      setBusinessDetails(customerToDisplay);
      setCustomerContacts(customerToDisplay.contacts || []);
      setCurrentContactIndex(0); // Reset contact index for new customer
      setCustomerNotes(customerToDisplay.notes || []);
      // DO NOT set customerSearch here automatically for initial load.
      // It's set when a user clicks a suggestion or if customers list becomes empty.
    } else if (customers.length > 0) {
      // If selectedCustomerId is somehow invalid or null, but customers exist,
      // default to the first customer.
      const firstCustomer = [...customers].sort(sortCustomersByName)[0];
      if (firstCustomer) {
        setSelectedCustomerId(firstCustomer.id);
        // businessDetails etc. will update in the next render cycle.
      }
    } else {
      // No customers, clear details and the input field
      setBusinessDetails({});
      setCustomerContacts([]);
      setCurrentContactIndex(0);
      setCustomerNotes([]);
      setCustomerSearch(""); // Clear input if no customers
    }
  }, [selectedCustomerId, customers]); // sortCustomersByName is a stable function

  // NEW: filter down to those whose name includes the search
  const customerSuggestions = useMemo(() => {
    if (!showSuggestions) return [];

    const baseList = customers.filter(c =>
      (selectedStoreType === "" || c.storeType === selectedStoreType)
    );

    if (customerSearch.trim() === "") {
      // If search input is blank, show all customers matching storeType filter
      return baseList.sort(sortCustomersByName);
    } else {
      // If search input has text, filter by that text
      return baseList
        .filter(c =>
          c.businessName.toLowerCase().includes(customerSearch.toLowerCase())
        )
        .sort(sortCustomersByName);
    }
  }, [customers, customerSearch, showSuggestions, selectedStoreType, sortCustomersByName]);

  // --- Scroll containment for the suggestions list ---
const listTouchYRef = useRef(0);

const handleListWheelCapture = (e) => {
  const el = dropdownRef.current;
  if (!el) return;

  // Always consume wheel and scroll the list itself
  el.scrollTop += e.deltaY;
  e.preventDefault();
  e.stopPropagation();
};

const handleListTouchStartCapture = (e) => {
  listTouchYRef.current = e.touches?.[0]?.clientY ?? 0;
};

const handleListTouchMoveCapture = (e) => {
  const el = dropdownRef.current;
  if (!el) return;

  const y = e.touches?.[0]?.clientY ?? 0;
  const dy = listTouchYRef.current - y; // swipe up -> positive
  listTouchYRef.current = y;

  el.scrollTop += dy;         // move the list
  e.preventDefault();         // stop page scroll
  e.stopPropagation();
};

  // Focus the dropdown when it becomes visible for better trackpad scrolling
  useEffect(() => {
    if (showSuggestions && dropdownRef.current) {
      // Small delay to ensure the dropdown is rendered before focusing
      setTimeout(() => {
        if (dropdownRef.current) {
          dropdownRef.current.focus();
        }
      }, 50);
    }
  }, [showSuggestions]);

  // When a new customer is selected, update related states.
  // This useEffect might be redundant if the one above handles all cases based on selectedCustomerId
  // useEffect(() => {
  //   if (selectedCustomer) {
  //     setBusinessDetails(selectedCustomer);
  //     setCustomerContacts(selectedCustomer.contacts || []);
  //     setCurrentContactIndex(0);
  //     setCustomerNotes(selectedCustomer.notes || []);
  //   }
  // }, [selectedCustomer]); // This selectedCustomer is derived, prefer selectedCustomerId as primary trigger

  // Chart data for ordering trends

  const chartData2 = {
    labels: businessDetails?.productTrends?.labels || [],
    datasets: [
      {
        label: "Orders",
        data: businessDetails?.productTrends?.data || [],
        fill: false,
        backgroundColor: "#737894",
        borderColor: "#737894"
      }
    ]
  };

  // ---------------- Business Details Editing Modal ----------------
  // 'editingBusiness' holds the current values for editing; if no id exists, we are in "add" mode.
  const [businessModalOpen, setBusinessModalOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [selectedTerritoryId, setSelectedTerritoryId] = useState(null);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false); // New state for loading indicator

  // useEffect(() => {
  //   // Only run this logic when editing, not adding
  //   if (editingBusiness?.id) {
  //     console.log("Editing business:", editingBusiness);
  //     const matchedTerritory = territories.find(t =>
  //       Array.isArray(t.assignCustomers?.id) &&
  //       t.assignCustomers.id.includes(editingBusiness.id)
  //     );
  //     console.log("Matched territory:", matchedTerritory);
  //     setSelectedTerritoryId(matchedTerritory?.name || null);
  //   }
  //   // do not reset selectedTerritoryId when adding — let it stay as-is
  // }, [editingBusiness, territories]);

  // Open the modal for editing the selected customer's business details.
const openBusinessEditModal = () => {
  // 1️⃣ Parse existing address or set up new fields
  let street = "", suburb = "", city = "", postCode = "";
  const currentAddressString = businessDetails.address;

  if (currentAddressString) {
    try {
      const parsedAddress = JSON.parse(currentAddressString);
      if (Array.isArray(parsedAddress) && parsedAddress.length >= 4) {
        street = parsedAddress[0] || "";
        suburb = parsedAddress[1] || "";
        city = parsedAddress[2] || "";
        postCode = parsedAddress[3] || "";
        // parsedAddress[4] is country, which is hardcoded below
      } else {
        // If it's not a valid array or old format, put everything in street for correction
        street = currentAddressString; // Treat as a single line street address
      }
    } catch (e) {
      // If JSON.parse fails, it's likely an old plain string address
      street = currentAddressString; // Treat as a single line street address
    }
  }

  // 2️⃣ Copy in all the current details and new address fields
  const { address, ...otherDetailsFromBusiness } = businessDetails; // Exclude old address string

  setEditingBusiness({ 
    ...otherDetailsFromBusiness, 
    street,
    suburb,
    city,
    postCode,
    country: "New Zealand", // Ensure country is set
  });

  // 3️⃣ Rehydrate the territory dropdown exactly as you had it
  let determinedTerritoryId = null;
  // — try numeric territoryId first
  if (
    businessDetails.territoryId != null &&
    !isNaN(Number(businessDetails.territoryId))
  ) {
    const idNum = Number(businessDetails.territoryId);
    if (territories.some(t => t.id === idNum)) {
      determinedTerritoryId = idNum;
    }
  }
  // — fallback to matching by name
  if (
    determinedTerritoryId === null &&
    typeof businessDetails.territory === "string"
  ) {
    const match = territories.find(t => t.name === businessDetails.territory);
    if (match) {
      determinedTerritoryId = match.id;
    }
  }
  setSelectedTerritoryId(determinedTerritoryId);

  // 4️⃣ **SIMPLIFIED**: pull your distribution straight from the business object
  //    (it’s already the string you want—e.g. "Pengellys")
  setSelectedDistribution(businessDetails.distribution || "");

  // 4️⃣ Open the modal
  setBusinessModalOpen(true);
};

  // Open the same modal in "add" mode (blank fields)
  const openAddCustomerModal = () => {
    setEditingBusiness({
      // No id yet: this signals a new customer.
      businessName: "",
      businessImage: "",
      contactEmail: "",
      phone: "",
      // address: "", // Old field replaced by structured address
      street: "",
      suburb: "",
      city: "",
      postCode: "",
      country: "New Zealand", // Hardcoded
      storeType: "",
      banner: "",
      directDebitActive: false,
      connectedStores: [],
      warningMessage: "",
      promotion: null,
      contacts: [],
      notes: [],
      orderHistory: [],
      productTrends: { labels: [], data: [] },
      products: [],
      rangedProducts: [],
      storeNumber: ""
    });
    setSelectedTerritoryId(null); // Reset territory ID
    setSelectedDistribution("");  // Reset distribution ID to empty string
    setBusinessModalOpen(true);
  };

  // When an image file is selected, create a temporary URL for preview.
  const handleBusinessImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditingBusiness({
        ...editingBusiness,
        businessImage: URL.createObjectURL(file)
      });
    }
  };

  const [selectedDistribution, setSelectedDistribution] = useState("");

  
  const [validationErrors, setValidationErrors] = useState({});

  // Generic handler for most input changes in the business edit modal
  const handleEditingBusinessInputChange = (fieldName, value) => {
    setEditingBusiness(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear validation error for this field if it exists
    if (validationErrors[fieldName]) {
      setValidationErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: null
      }));
    }
  };

  // Archive/Unarchive Customer Modal Handlers
  const openArchiveCustomerConfirmModal = () => setArchiveCustomerConfirmModalOpen(true);
  const closeArchiveCustomerConfirmModal = () => setArchiveCustomerConfirmModalOpen(false);

  const handleConfirmCustomerArchive = async () => {
    if (!editingBusiness || !editingBusiness.id) return;

    const newArchivedStatus = !editingBusiness.archived;
    const updatedCustomerData = { ...editingBusiness, archived: newArchivedStatus };

    // Optimistic UI update
    setEditingBusiness(updatedCustomerData); // Update the form data

    const originalCustomers = data.customers;
    setData(d => ({
      ...d,
      customers: d.customers.map(c =>
        c.id === editingBusiness.id ? updatedCustomerData : c
      ),
    }));

    try {
      await updateCustomer(editingBusiness.id, { archived: newArchivedStatus }); // API call
      // If API call is successful, the optimistic update is correct.
      // Optionally, refresh businessDetails if it's separate from editingBusiness
      if (selectedCustomerId === editingBusiness.id) {
        setBusinessDetails(updatedCustomerData);
      }
    } catch (error) {
      // console.error(`Failed to ${newArchivedStatus ? "archive" : "unarchive"} customer:`, error);
      // Revert optimistic update if API call fails
      setEditingBusiness(editingBusiness); // Revert form data
      setData(d => ({
        ...d,
        customers: originalCustomers,
      }));
      // Optionally, display an error message to the user here
      setNotif({ type: "error", message: `Failed to ${newArchivedStatus ? "archive" : "unarchive"} customer.` });
    }

    closeArchiveCustomerConfirmModal();
    setBusinessModalOpen(false); // Close the main edit modal as well
  };


  const handleSaveBusinessEdit = async () => {
  // 1) Validate inputs
  const errors = {};
  if (!editingBusiness.businessName?.trim()) {
    errors.businessName = "Business name is required";
  }
  if (!editingBusiness.storeType?.trim()) {
    errors.storeType = "Store type is required";
  }
  if (!editingBusiness.banner?.trim()) {
    errors.banner = "Banner is required";
  }

  // Territory validation
  if (selectedTerritoryId === null || selectedTerritoryId === undefined) {
    errors.territory = "Territory is required";
  }

  // Distribution validation: must be a non-empty string
  const dist = editingBusiness.distribution?.trim();
  if (!dist) {
     errors.distribution = "Distribution is required";
   }

  // New Address Field Validation
  if (!editingBusiness.street?.trim()) {
    errors.street = "Street is required";
  }
  if (!editingBusiness.city?.trim()) {
    errors.city = "City/Town is required";
  }
  if (!editingBusiness.postCode?.trim()) {
    errors.postCode = "Post Code is required";
  }
  // Suburb is optional, Country is hardcoded

  if (Object.keys(errors).length > 0) {
    setValidationErrors(errors);
    return;
  }
  setValidationErrors({}); // clear any existing errors
  setIsSavingCustomer(true); // Set loading state to true

  // 2) Build payload for the API
  const isEditing = Boolean(editingBusiness.id);

  // Map your territory dropdown back into a number or null
  const territoryApiValue = selectedTerritoryId; // Already a number or null

  // Preserve the current archived status from the form, it should not be changed by a general save.
  const currentArchivedStatus = editingBusiness.archived;

  // Construct the address array and stringify it
  const addressArray = [
    editingBusiness.street || "",
    editingBusiness.suburb || "",
    editingBusiness.city || "",
    editingBusiness.postCode || "",
    "New Zealand" // Hardcoded country
  ];
  const addressString = JSON.stringify(addressArray);

  // Create a mutable copy of editingBusiness for the payload
  const payloadData = { ...editingBusiness };

  // Set the stringified address
  payloadData.address = addressString;

  // Remove individual address components and other non-payload fields
  delete payloadData.street;
  delete payloadData.suburb;
  delete payloadData.city;
  delete payloadData.postCode;
  delete payloadData.country;
  // delete payloadData.archived; // Do not send 'archived' status during a general save - this should be handled by not including it if it's not meant to be updated here.
  // The API should only update fields that are explicitly sent.
  // Let's ensure the main payload object is built correctly with only intended fields.

  const finalPayload = {
    businessName: payloadData.businessName,
    businessImage: payloadData.businessImage, // Ensure this is handled (URL or file object)
    contactEmail: payloadData.contactEmail,
    phone: payloadData.phone,
    address: payloadData.address, // The new stringified address
    storeType: payloadData.storeType,
    banner: payloadData.banner,
    directDebitActive: payloadData.directDebitActive,
    connectedStores: payloadData.connectedStores || [],
    warningMessage: payloadData.warningMessage,
    promotion: payloadData.promotion, // Assuming this is correctly structured
    storeNumber: payloadData.storeNumber,
    territory: territoryApiValue,
    distribution: dist, // Use the trimmed distribution value
    // id is not part of create payload, and for update, it's in the URL
  };
  
  // If it's an existing customer and we are not changing the archived status with this save,
  // we should not send the 'archived' field at all, or ensure it's the current value if the API expects it.
  // For simplicity, if 'archived' is not meant to be updated here, it's often omitted from the payload.
  // However, if your API updates all fields provided and nulls others, you might need to include it.
  // The previous logic `delete payload.archived` was correct if you don't want to update it here.
  // The `savedCustomerDataFromAPI` later restores it from `currentArchivedStatus`.

  // console.log(
  //   isEditing
  //     ? "Updating customer with payload:"
  //     : "Creating customer with payload:",
  //   finalPayload // Use finalPayload here
  // );
  // console.log("payload", finalPayload); // Redundant

  try {
    // 3) Send to back end
    let savedCustomerDataFromAPI;
    if (isEditing) {
      savedCustomerDataFromAPI = await updateCustomer(editingBusiness.id, finalPayload);
    } else {
      savedCustomerDataFromAPI = await createCustomer(finalPayload);
    }

    // 4) Update your local state
    const customerForState = {
      // Start with the data as it is in editingBusiness (which includes street, city etc.)
      ...editingBusiness, 
      // Overlay with whatever the API returns (this might include the stringified address)
      ...savedCustomerDataFromAPI, 
      // Ensure the stringified address from our construction is what's in the state
      address: addressString, 
      archived: currentArchivedStatus, 
      id: isEditing
        ? editingBusiness.id
        : savedCustomerDataFromAPI?.id || Date.now(), // Use API ID if returned
      territoryId: territoryApiValue, // Store the ID used for the save
      // Explicitly remove individual address components from the main customer object in the list,
      // as the canonical address is now the stringified version.
    };
    delete customerForState.street;
    delete customerForState.suburb;
    delete customerForState.city;
    delete customerForState.postCode;
    delete customerForState.country;


    const updatedCustomers = isEditing
      ? customers.map(c => (c.id === customerForState.id ? customerForState : c))
      : [...customers, customerForState];

    // Also update your territories assignment if you need to keep that in sync:
    const updatedTerritories = territories.map(t => {
      const ids = Array.isArray(t.assignCustomers?.id)
        ? t.assignCustomers.id
        : [];
      const filtered = ids.filter(cid => cid !== customerForState.id);
      if (t.id === territoryApiValue) {
        filtered.push(customerForState.id);
      }
      return { ...t, assignCustomers: { id: filtered } };
    });

    setData({
      ...data,
      customers: updatedCustomers,
      territories: updatedTerritories,
    });

    // Close modal & reset editing state
    setBusinessDetails(customerForState);
    setSelectedCustomerId(customerForState.id);
    setBusinessModalOpen(false);
    setEditingBusiness(null);
  } catch (err) {
    console.error("Error saving customer:", err);
    setValidationErrors({
      apiError: err.message || "Failed to save customer. Please try again.",
    });
  } finally {
    setIsSavingCustomer(false); // Reset loading state
  }
};


  
  

  const handleCancelBusinessEdit = () => {
    setBusinessModalOpen(false);
    setEditingBusiness(null);
    setValidationErrors({}); // Clear validation errors
  };

  // ---------------- Contacts State & Modal ----------------
  const [customerContacts, setCustomerContacts] = useState(selectedCustomer.contacts || []);
  const [currentContactIndex,  setCurrentContactIndex] = useState(0);

  const currentContact = customerContacts[currentContactIndex] || null;

  const handleNextContact = () => {
    if (customerContacts.length > 1) {
      setCurrentContactIndex(
        (prev) => (prev + 1) % customerContacts.length
      );
    }
  };

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [confirmDeleteContactOpen, setConfirmDeleteContactOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(false);
  const invalidName = contactModalOpen && (!editingContact?.name || !editingContact.name.trim());
  const openEditContactModal = () => {
    if (currentContact) {
      setEditingContact({ ...currentContact });
      setContactModalOpen(true);
    }
  };

  const openAddContactModal = () => {
    setEditingContact({ name: "", email: "", phone: "", title: "" });
    setContactModalOpen(true);
  };

const handleSaveContact = async () => {
  if (!editingContact.name) return;

  const isEditing = Boolean(editingContact.id);
  let tempId = editingContact.id;
  const payload = {
    customerId: selectedCustomerId,
    ...editingContact,
  };

  // 1) Optimistically update local contacts
  setCustomerContacts(prev => {
    const updated = isEditing
      ? prev.map(c => (c.id === tempId ? payload : c))
      : [...prev, { ...payload, id: tempId = Date.now() }];
    // ALSO push that change into DataContext right away:
    setData(d => {
      const updatedCustomers = d.customers.map(cust =>
        cust.id === selectedCustomerId
          ? { ...cust, contacts: updated }
          : cust
      );
      return { ...d, customers: updatedCustomers };
    });
    return updated;
  });

  // 2) Close the modal
  setContactModalOpen(false);
  setEditingContact(null);

  // 3) Fire off API call
  try {
    if (isEditing) {
      await updateContact(tempId, payload);
    } else {
      const saved = await createContact(payload);
      // replace tempId with real one
      setCustomerContacts(prev => {
        const replaced = prev.map(c => (c.id === tempId ? saved : c));
        // and update context again with the real ID
        setData(d => {
          const updatedCustomers = d.customers.map(cust =>
            cust.id === selectedCustomerId
              ? { ...cust, contacts: replaced }
              : cust
          );
          return { ...d, customers: updatedCustomers };
        });
        return replaced;
      });
    }
  } catch (err) {
    console.error("Contact save failed:", err);
    // rollback local & context
    setCustomerContacts(prev =>
      isEditing
        ? prev
        : prev.filter(c => c.id !== tempId)
    );
    setData(d => {
      // reload original contacts from context
      const originalCust = customers.find(c => c.id === selectedCustomerId);
      const updatedCustomers = d.customers.map(cust =>
        cust.id === selectedCustomerId
          ? { ...cust, contacts: originalCust.contacts }
          : cust
      );
      return { ...d, customers: updatedCustomers };
    });
  }
};


  const handleEditNote = (noteId) => {
    const noteToEdit = customerNotes.find((note) => note.id === noteId);
    if (noteToEdit) {
      setEditingNoteId(noteId);
      setEditedNoteContent(noteToEdit.content);
    }
  };

  const handleCancelContactEdit = () => {
    setContactModalOpen(false);
    setEditingContact(null);
  };

  const handleDeleteContactClick = () => {
    setConfirmDeleteContactOpen(true);
  };

  const handleCancelDeleteContact = () => {
    setConfirmDeleteContactOpen(false);
  };

  const handleConfirmDeleteContact = async () => {
    if (!editingContact || !editingContact.id) return;

    const contactId = editingContact.id;
    const deletedContactIndex = customerContacts.findIndex(c => c.id === contactId);

    // 1) Store original values for potential rollback
    const originalContacts = [...customerContacts];
    const originalContactIndex = currentContactIndex;

    // 2) Calculate new contact index before updating the array
    const updatedContacts = customerContacts.filter(c => c.id !== contactId);
    let newIndex = 0;
    
    if (updatedContacts.length > 0) {
      if (deletedContactIndex <= currentContactIndex) {
        // If we deleted a contact at or before the current index, adjust the index
        newIndex = Math.max(0, currentContactIndex - 1);
      } else {
        // If we deleted a contact after the current index, keep the same index
        newIndex = currentContactIndex;
      }
      
      // Ensure the new index is valid
      if (newIndex >= updatedContacts.length) {
        newIndex = updatedContacts.length - 1;
      }
    }

    // 3) Update both contacts and index atomically (optimistic update)
    setCustomerContacts(updatedContacts);
    setCurrentContactIndex(newIndex);

    // 4) Update DataContext
    setData(d => {
      const updatedCustomers = d.customers.map(cust =>
        cust.id === selectedCustomerId
          ? { ...cust, contacts: updatedContacts }
          : cust
      );
      return { ...d, customers: updatedCustomers };
    });

    // 5) Close modals after state updates
    setConfirmDeleteContactOpen(false);
    setContactModalOpen(false);
    setEditingContact(null);

    // 6) Fire API call
    try {
      setDeletingContact(true);
      await deleteContact(contactId);
      setNotif({ type: "success", message: "Contact deleted successfully." });
    } catch (err) {
      console.error("Failed to delete contact:", err);
      // Rollback optimistic updates using stored original values
      setCustomerContacts(originalContacts);
      setCurrentContactIndex(originalContactIndex);
      setData(d => {
        const updatedCustomers = d.customers.map(cust =>
          cust.id === selectedCustomerId
            ? { ...cust, contacts: originalContacts }
            : cust
        );
        return { ...d, customers: updatedCustomers };
      });
      setNotif({ type: "error", message: "Could not delete contact. Please try again." });
    } finally {
      setDeletingContact(false);
    }
  };

  // ---------------- Notes State & Handlers ----------------
  const [customerNotes, setCustomerNotes] = useState(
    Array.isArray(selectedCustomer.notes) ? selectedCustomer.notes : []
  );
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedNoteContent, setEditedNoteContent] = useState("");

  const displayedNotes =
    customerNotes.length > 5 && !notesExpanded
      ? customerNotes.slice(0, 5)
      : customerNotes;

  const toggleNotesExpanded = () => {
    if (isArchived) return;
    setNotesExpanded(!notesExpanded);
  };

// Add a new note
const handleAddNote = async () => {
  if (isArchived || !newNoteContent.trim()) return;

  const tempId   = Date.now();
  const timestamp = new Date().toISOString();
  const newNote  = {
    id:          tempId,
    customerId:  selectedCustomer.id,
    authorName:  userProfile.name,
    authorImage: userProfile.image || placeholder,
    content:     newNoteContent.trim(),
    timestamp
  };

  // 1) Local state
  const newNotes = [ newNote, ...customerNotes ];
  setCustomerNotes(newNotes);
  setNewNoteContent("");

  // 2) ALSO push into DataContext.customers[x].notes
  setData(d => {
    const updatedCustomers = d.customers.map(c =>
      c.id === selectedCustomer.id
        ? { ...c, notes: newNotes }
        : c
    );
    return { ...d, customers: updatedCustomers };
  });

  // 3) Persist, then swap out tempId for real one
  try {
    const saved = await createNote(newNote);
    const replacedNotes = newNotes.map(n =>
      n.id === tempId ? saved : n
    );
    setCustomerNotes(replacedNotes);

    // update context again
    setData(d => {
      const updatedCustomers = d.customers.map(c =>
        c.id === selectedCustomer.id
          ? { ...c, notes: replacedNotes }
          : c
      );
      return { ...d, customers: updatedCustomers };
    });
  } catch (err) {
    console.error("Failed to create note:", err);
    // rollback both local and context
    setCustomerNotes(notes => notes.filter(n => n.id !== tempId));
    setData(d => {
      const updatedCustomers = d.customers.map(c =>
        c.id === selectedCustomer.id
          ? { ...c, notes: (selectedCustomer.notes || []) }
          : c
      );
      return { ...d, customers: updatedCustomers };
    });
  }
};

// Save edits to an existing note
const handleSaveEdit = async (noteId) => {
  if (isArchived || !editedNoteContent.trim()) return;

  // find original so we can roll back on failure
  const originalNotes = [...customerNotes];
  const updatedNotes  = customerNotes.map(n =>
    n.id === noteId
      ? { ...n, content: editedNoteContent.trim() }
      : n
  );

  // 1) Local state
  setCustomerNotes(updatedNotes);
  setEditingNoteId(null);
  setEditedNoteContent("");

  // 2) Context
  setData(d => {
    const updatedCustomers = d.customers.map(c =>
      c.id === selectedCustomer.id
        ? { ...c, notes: updatedNotes }
        : c
    );
    return { ...d, customers: updatedCustomers };
  });

  // 3) Persist
  try {
    await updateNote(noteId, { content: updatedNotes.find(n => n.id === noteId).content });
  } catch (err) {
    console.error("Failed to update note:", err);
    // rollback both local and context
    setCustomerNotes(originalNotes);
    setData(d => {
      const updatedCustomers = d.customers.map(c =>
        c.id === selectedCustomer.id
          ? { ...c, notes: originalNotes }
          : c
      );
      return { ...d, customers: updatedCustomers };
    });
  }
};

// Delete a note (user only)
const handleDeleteNote = async (noteId) => {
  if (isArchived) return;

  // keep a copy for rollback
  const originalNotes = [...customerNotes];

  // 1️⃣ Optimistically remove from local state
  const updatedNotes = originalNotes.filter(n => n.id !== noteId);
  setCustomerNotes(updatedNotes);
  setEditingNoteId(null);
  setEditedNoteContent("");

  // 2️⃣ Optimistically update the DataContext
  setData(d => ({
    ...d,
    customers: d.customers.map(c =>
      c.id === selectedCustomer.id
        ? { ...c, notes: updatedNotes }
        : c
    )
  }));

  // 3️⃣ Fire the API call
  try {
    await deleteNote(noteId);
  } catch (err) {
    console.error("Failed to delete note:", err);

    // rollback local state
    setCustomerNotes(originalNotes);

    // rollback context
    setData(d => ({
      ...d,
      customers: d.customers.map(c =>
        c.id === selectedCustomer.id
          ? { ...c, notes: originalNotes }
          : c
      )
    }));
  }
};


  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditedNoteContent("");
  };

  // ---------------- Order Modal & Handlers ----------------
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  const handleOrderClick = async (order) => {
    if (isArchived) return;

    setSelectedOrder(order);      // to keep track of which one
    setOrderDetails(null);
    setLoadingOrder(true);
    setOrderModalOpen(true);

    try {
      const details = await fetchOrderDetails(order.orderId);
      setOrderDetails(details);
    } catch (err) {
      console.error("Failed to load order:", err);
      // you could set an error state here if you like
    } finally {
      setLoadingOrder(false);
    }
  };


  const toggleOrderModal = () => {
    setOrderModalOpen(!orderModalOpen);
  };

  const toggleEditOrderModal = () => {
    if (editOrderModalOpen) {
      setOrderBeingEdited(null);
    }
    setEditOrderModalOpen(!editOrderModalOpen);
  };

  const handleEditClick = () => {
    setConfirmEditOpen(true);
  };

  const handleCancelEditOrder = () => {
    setConfirmEditOpen(false);
  };

  const handleConfirmEditOrder = () => {
    if (!orderDetails) return;

    setConfirmEditOpen(false);
    setOrderBeingEdited(orderDetails);
    setOrderModalOpen(false);
    setEditOrderModalOpen(true);
  };

  const getOrderDetails = (order) => order?.items || [];

    // ---------------- New Order Modal (Reusable Component) ----------------
    const [addOrderModalOpen, setAddOrderModalOpen] = useState(false);

    const openAddOrderModal = () => {
      setAddOrderModalOpen(true);
    };
  
    const toggleAddOrderModal = () => {
      setAddOrderModalOpen(!addOrderModalOpen);
    };
  const detailItems = getNormalizedItems(selectedOrder);
const handleAddOrderSave = (newOrder) => {
  const updatedCustomers = syncOrderIntoCustomers(customers, newOrder);
  const updatedBusinessDetails =
    updatedCustomers.find((customer) => customer.id === selectedCustomerId) ||
    businessDetails;

  setBusinessDetails(updatedBusinessDetails);
  setData({ ...data, customers: updatedCustomers });
  setAddOrderModalOpen(false);
  setEditOrderModalOpen(false);
  setOrderBeingEdited(null);
  setSelectedOrder(null);
  setOrderDetails(null);
};

const [sortedOrders, setSortedOrders] = useState([]);
const orders = selectedCustomer.orderHistory || [];
// console.log("orders:", orders); 
useEffect(() => {
  // Keep edited orders in their original order-date position.
  const history = Array.isArray(businessDetails.orderHistory)
    ? businessDetails.orderHistory
    : [];
  const sorted = [...history].sort((a, b) => {
    const timestampA = new Date(getOrderActivityTimestamp(a) || 0).getTime();
    const timestampB = new Date(getOrderActivityTimestamp(b) || 0).getTime();
    if (timestampB !== timestampA) return timestampB - timestampA;
    return Number(b.orderId) - Number(a.orderId);
  });
  setSortedOrders(sorted);
}, [businessDetails.orderHistory]);

    // just above your <Table> render
const filteredOrders = sortedOrders
  .filter(o => {
    const d = new Date(o.date);
    if (startDate && d < startDate) return false;
    if (endDate   && d > endDate)   return false;
    return true;
  });

  // 1. Compute last 12 months and revenue sums
  const revenueByMonth = useMemo(() => {
    // build an array of Date objects for the 1st of each month, 11 months ago → now
    const now = new Date();
    const months = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
      return d;
    });

    // accumulate order totals
    
    const revMap = months.reduce((acc, d) => {
      acc[`${d.getFullYear()}-${d.getMonth()+1}`] = 0;
      return acc;
    }, {});

    // now COUNT ALL of that customer’s orders
    orders.forEach(o => { // 'orders' is selectedCustomer.orderHistory || []
      const d   = new Date(o.date);
      const key = `${d.getFullYear()}-${d.getMonth()+1}`;
      if (key in revMap) revMap[key] += parseFloat(o.total || 0); // MODIFIED: Sum revenue
    });

    // build labels (“Jun 2024”, …) and data array in order
    const labels = months.map(d =>
      d.toLocaleString("default", { month: "short", year: "numeric" })
    );
    const dataArr = months.map(
      d => revMap[`${d.getFullYear()}-${d.getMonth()+1}`]
    );

    return { labels, data: dataArr };
  }, [orders, selectedCustomerId]);

   const orderByMonth = useMemo(() => {
    // build an array of Date objects for the 1st of each month, 11 months ago → now
    const now = new Date();
    const months = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
      return d;
    });

    // accumulate order totals
    
    const revMap = months.reduce((acc, d) => {
      acc[`${d.getFullYear()}-${d.getMonth()+1}`] = 0;
      return acc;
    }, {});

    // now COUNT ALL of that customer’s orders
    orders.forEach(o => {
      const d   = new Date(o.date);
      const key = `${d.getFullYear()}-${d.getMonth()+1}`;
      if (key in revMap) revMap[key] += 1; // Changed to count orders
    });

    // build labels (“Jun 2024”, …) and data array in order
    const labels = months.map(d =>
      d.toLocaleString("default", { month: "short", year: "numeric" })
    );
    const dataArr = months.map(
      d => revMap[`${d.getFullYear()}-${d.getMonth()+1}`]
    );

    return { labels, data: dataArr };
  }, [orders, selectedCustomerId]);

  // 2. Chart configuration
  const revenueChartData = {
    labels: revenueByMonth.labels,
    datasets: [
      {
        label: "Revenue",
        data: revenueByMonth.data,
        fill: false,
        backgroundColor: "#737894",
        borderColor: "#737894",
      },
    ],
  };

    const orderChartData = {
    labels: orderByMonth.labels,
    datasets: [
      {
        label: "Orders",
        data: orderByMonth.data,
        fill: false,
        backgroundColor: "#737894",
        borderColor: "#737894",
      },
    ],
  };


// 1. After you build `revenueByMonth.data`:
const allVals     = revenueByMonth.data;
const averageMonthlyRevenue = allVals.length
  ? allVals.reduce((sum, value) => sum + Number(value || 0), 0) / allVals.length
  : 0;
const rawMax      = Math.max(...allVals, 0);
// round up to nearest 1 000, or default to 1 000 if max is 0
const yAxisMax    = rawMax === 0
  ? 1000
  : Math.ceil(rawMax / 1000) * 1000;


const orderYAxisMax = useMemo(() => {
  const maxCount = Math.max(...orderByMonth.data, 0);
  // if you want “nice” round numbers at the top:
  const step = 5; 
  return maxCount === 0
    ? step
    : Math.ceil(maxCount / step) * step;
}, [orderByMonth.data]);
const averageMonthlyOrders = orderByMonth.data.length
  ? orderByMonth.data.reduce((sum, value) => sum + Number(value || 0), 0) / orderByMonth.data.length
  : 0;

// 2. Chart options
const revenueChartOptions = {
  maintainAspectRatio: false,
  scales: {
    y: {
      min: 0,
      max: yAxisMax,
      ticks: {
        stepSize: 200,
        callback: (v) => `$${v.toLocaleString()}`,
      },
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const v = ctx.parsed.y;
          return `Revenue: $${v.toLocaleString()}`;
        },
      },
    },
  },
};

const orderChartOptions = {
  maintainAspectRatio: false,
  scales: {
    y: {
      min: 0,
      max: orderYAxisMax,
      ticks: {
        stepSize: Math.ceil(orderYAxisMax / 5),  // or hard-code e.g. 1 or 5
      },
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: ctx => `Orders: ${ctx.parsed.y}`,
      },
    },
  },
};


  // Connected Stores select options & aggregated value container
  const customerStoreOptions = useMemo(() => (
    customers
      .filter(c => !editingBusiness || c.id !== editingBusiness.id) // exclude self when editing
      .map(c => ({ value: c.id, label: c.businessName }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  ), [customers, editingBusiness]);

  const ConnectedStoresValueContainer = (props) => {
    const { getValue, hasValue } = props;
    const values = getValue();
    if (!hasValue || values.length === 0) {
      return <selectComponents.ValueContainer {...props} />;
    }
    const storeNames = values.map(v => v.label).join(', ');
    return (
      <selectComponents.ValueContainer {...props}>
        {storeNames}
      </selectComponents.ValueContainer>
    );
  };

  // Connected Stores editing state (separate card)
  const [connectedStoresSelection, setConnectedStoresSelection] = useState(businessDetails.connectedStores || []);
  useEffect(() => {
    setConnectedStoresSelection(businessDetails.connectedStores || []);
  }, [businessDetails.connectedStores]);

  // Helpers and state for tab-like add/remove UI
  const selectedStoreOptions = useMemo(
    () => customerStoreOptions.filter(opt => (connectedStoresSelection || []).includes(opt.value)),
    [customerStoreOptions, connectedStoresSelection]
  );
  const availableStoreOptions = useMemo(
    () => customerStoreOptions.filter(opt => !(connectedStoresSelection || []).includes(opt.value)),
    [customerStoreOptions, connectedStoresSelection]
  );
  const [addStoreValue, setAddStoreValue] = useState(null);
  const addConnectedStore = (option) => {
    if (!option) return;
    const id = option.value;
    setConnectedStoresSelection(prev => (prev.includes(id) ? prev : [...prev, id]));
    setAddStoreValue(null);
  };
  const removeConnectedStore = (id) => {
    setConnectedStoresSelection(prev => prev.filter(v => v !== id));
  };

  const handleSaveConnectedStores = async () => {
    try {
      const payload = { connectedStores: connectedStoresSelection };
      const saved = await updateCustomer(businessDetails.id, payload);

      // Update local state and DataContext
      const updated = { ...businessDetails, connectedStores: connectedStoresSelection };
      setBusinessDetails(updated);
      setData(d => ({
        ...d,
        customers: (d.customers || []).map(c => c.id === businessDetails.id ? { ...c, connectedStores: connectedStoresSelection } : c)
      }));
      setNotif({ type: 'success', message: 'Connected stores updated.' });
    } catch (err) {
      console.error('Failed to update connected stores:', err);
      setNotif({ type: 'error', message: 'Could not update connected stores. Please try again.' });
    }
  };

  const handleCancelConnectedStores = () => {
    setConnectedStoresSelection(businessDetails.connectedStores || []);
    setAddStoreValue(null);
  };

  // Ranged Products editing state (separate card)
  const [rangedProductSelection, setRangedProductSelection] = useState(
    Array.isArray(businessDetails.rangedProducts)
      ? businessDetails.rangedProducts.map(normalizeProductId)
      : []
  );
  const [addRangeProductValue, setAddRangeProductValue] = useState(null);
  const [isSavingRangeProducts, setIsSavingRangeProducts] = useState(false);

  useEffect(() => {
    setRangedProductSelection(
      Array.isArray(businessDetails.rangedProducts)
        ? businessDetails.rangedProducts.map(normalizeProductId)
        : []
    );
    setAddRangeProductValue(null);
  }, [businessDetails.id, businessDetails.rangedProducts]);

  const productById = useMemo(() => {
    return new Map(products.map(product => [normalizeProductId(product.id), product]));
  }, [products]);

  const brandNameById = useMemo(() => {
    return new Map((data.brands || []).map(brand => [String(brand.id), brand.name]));
  }, [data.brands]);

  const rangedProductIds = useMemo(() => {
    const seen = new Set();
    return (rangedProductSelection || [])
      .map(normalizeProductId)
      .filter(id => {
        if (id == null || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [rangedProductSelection]);

  const rangedProductIdSet = useMemo(
    () => new Set(rangedProductIds),
    [rangedProductIds]
  );

  const getProductBrandName = (product) => {
    if (!product) return "";
    if (product.brand?.name) return product.brand.name;
    if (product.brandName) return product.brandName;
    const brandId = product.brandId || product.brand_id || product.brand?.id;
    return brandNameById.get(String(brandId)) || "";
  };

  const rangeProductOptions = useMemo(() => (
    products
      .filter(product => !(product.archived || product.is_archived))
      .map(product => {
        const brandId = product.brandId || product.brand_id || product.brand?.id;
        const brandName = product.brand?.name || product.brandName || brandNameById.get(String(brandId)) || "";
        return {
          value: normalizeProductId(product.id),
          label: brandName ? `${product.name} (${brandName})` : product.name,
          product,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }))
  ), [products, brandNameById]);

  const availableRangeProductOptions = useMemo(
    () => rangeProductOptions.filter(option => !rangedProductIdSet.has(option.value)),
    [rangeProductOptions, rangedProductIdSet]
  );

  const rangedProducts = useMemo(() => (
    rangedProductIds
      .map(id => productById.get(id))
      .filter(Boolean)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
  ), [rangedProductIds, productById]);

  const addRangedProduct = (option) => {
    if (!option) return;
    const id = normalizeProductId(option.value);
    setRangedProductSelection(prev => (
      prev.map(normalizeProductId).includes(id) ? prev : [...prev, id]
    ));
    setAddRangeProductValue(null);
  };

  const removeRangedProduct = (id) => {
    const productId = normalizeProductId(id);
    setRangedProductSelection(prev => prev.map(normalizeProductId).filter(value => value !== productId));
  };

  const handleSaveRangedProducts = async () => {
    if (!businessDetails.id) return;

    try {
      setIsSavingRangeProducts(true);
      const saved = await updateCustomerRange(businessDetails.id, rangedProductIds);
      const savedProductIds = (saved.productIds || rangedProductIds).map(normalizeProductId);
      const updated = { ...businessDetails, rangedProducts: savedProductIds };

      setBusinessDetails(updated);
      setRangedProductSelection(savedProductIds);
      setData(d => ({
        ...d,
        customers: (d.customers || []).map(c =>
          c.id === businessDetails.id ? { ...c, rangedProducts: savedProductIds } : c
        )
      }));
      setNotif({ type: "success", message: "Ranged products updated." });
    } catch (err) {
      console.error("Failed to update ranged products:", err);
      setNotif({ type: "error", message: "Could not update ranged products. Please try again." });
    } finally {
      setIsSavingRangeProducts(false);
    }
  };

  const handleCancelRangedProducts = () => {
    setRangedProductSelection(
      Array.isArray(businessDetails.rangedProducts)
        ? businessDetails.rangedProducts.map(normalizeProductId)
        : []
    );
    setAddRangeProductValue(null);
  };

  return (
    <div className="content">
            {notif.message && (
        <Alert
          color={notif.type === "error" ? "danger" : "success"}
          toggle={() => setNotif({ type: "", message: "" })}
        >
          {notif.message}
        </Alert>
      )}
      {isArchived && (
        <Alert color="warning" className="mb-3">
          <strong>This customer is archived.</strong> Most actions are disabled. You can unarchive this customer by editing their business details.
        </Alert>
      )}
      {/* Customer Selector */}
      <Row className="align-items-center">
                <Col md="2">
          <FormGroup>
            <Label for="storeTypeFilter"><strong>Store Type</strong></Label>
            <Input
              type="select"
              id="storeTypeFilter"
              value={selectedStoreType}
              onChange={e => setSelectedStoreType(e.target.value)}
            >
              <option value="">All Store Types</option>
              {storeTypeOptions
                .filter(type => !type.is_archived)
                .map((type, idx) => (
                  <option key={idx} value={type.name}>
                    {type.name}
                  </option>
                ))
              }
            </Input>
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup style={{ position: "relative" }}>
            <Label for="customerSearch"><strong>Select Customer</strong></Label>
            <Input
              type="text"
              id="customerSearch"
              placeholder="Start typing to search…"
              autoComplete="off"
              value={customerSearch}
              // onBlur={() => {
              //   // Hide suggestions as soon as the input loses focus
              //   setShowSuggestions(false);
              // }}
              onFocus={() => {
                // as soon as they focus (or click) on the input, show suggestions
                setShowSuggestions(true);
              }}
              onChange={e => setCustomerSearch(e.target.value)}
            />
        {showSuggestions && customerSuggestions.length > 0 && (
          <ul
            ref={dropdownRef}
            className="list-group position-absolute w-100"
            style={{
              zIndex: 1000,
              maxHeight: 200,
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",   // <-- stop scroll chaining
              touchAction: "manipulation",     // <-- allow natural touch scroll
              outline: "none"
            }}
            tabIndex={0}
            onWheelCapture={handleListWheelCapture}
            onTouchStartCapture={handleListTouchStartCapture}
            onTouchMoveCapture={handleListTouchMoveCapture}
            onKeyDown={(e) => {
              if (e.key === "Escape") setShowSuggestions(false);
            }}
          >
            {customerSuggestions.map(cust => (
              <li
                key={cust.id}
                className="list-group-item list-group-item-action"
                style={{
                  color: cust.archived === true ? 'rgba(73, 80, 87, 0.5)' : 'rgba(73, 80, 87)',
                  backgroundColor: cust.archived === true ? '#f8d7da' : 'white',
                  cursor: "pointer"
                }}
                onClick={() => {
                  setSelectedCustomerId(cust.id);
                  setCustomerSearch(cust.businessName);
                  setShowSuggestions(false);
                }}
              >
                {cust.businessName}
              </li>
            ))}
          </ul>
        )}
          </FormGroup>
        </Col>

        {isSuperuser && (
        <Col md="6" className="d-flex justify-content-end">
          <NavItem style={{ listStyleType: "none" }}>
            <NavLink
              href="#!"
              onClick={openAddCustomerModal}
              className="d-flex flex-column align-items-center"
            >
              <i
                className="nc-icon nc-delivery-fast text-center"
                style={{ fontSize: "1.5rem" }}
                title="Add Customer"
              />
              <p className="text-center mt-1" style={{ textTransform: "none" }}>
                Add Customer
              </p>
            </NavLink>
          </NavItem>
        </Col>
        )}
      </Row>

      {/* Business & Contact Details */}
      <Row>
        <Col md="6">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h4" className="mb-0">Business Details</CardTitle>
              {isSuperuser && (
              <Button color="primary" onClick={openBusinessEditModal}>
                Edit
              </Button>
              )}
            </CardHeader>
            <CardBody style={isArchived ? { opacity: 0.6 } : {}}>
              <div className="d-flex align-items-center mb-3">
                <img
                  src={customerBannerUrl}
                  alt={businessDetails.businessName}
                  className="rounded-circle"
                  style={{
                    width: "80px",
                    height: "80px",
                    marginRight: "20px",
                    objectFit: "cover",
                    objectPosition: "top",
                  }}
                  onError={e => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = placeholder;
                  }}
                />
                <div>
                  <h5 className="mb-1" style={{ fontWeight: "bold" }}>
                    {businessDetails.businessName}
                  </h5>
                  <p className="text-muted mb-0">{businessDetails.storeType}</p>
                  {isArchived && <p className="text-danger mb-0" style={{fontWeight: "bold"}}>(Archived)</p>}
                </div>
              </div>
              <hr />
              <p>
                <strong>Store #:</strong> {businessDetails.storeNumber}
              </p>
              <hr />
              <p>
                <strong>Email:</strong> {businessDetails.contactEmail}
              </p>
              <hr />
              <p>
                <strong>Phone:</strong> {businessDetails.phone}
              </p>
              <div>
                <strong>Address:</strong>
                {(() => {
                  let addressContent;
                  if (businessDetails.address) {
                    try {
                      const parsedAddress = JSON.parse(businessDetails.address);
                      if (Array.isArray(parsedAddress)) {
                        const labels = ["Street", "Suburb", "City/Town", "Post Code", "Country"];
                        const addressComponents = parsedAddress.map((value, index) => ({
                          label: labels[index],
                          value: value
                        })).filter(component => component.value && String(component.value).trim() !== "");

                        if (addressComponents.length > 0) {
                          addressContent = (
                            <div style={{ marginLeft: '10px' }}>
                              {addressComponents.map((component, idx) => (
                                <div key={idx}>
                                  <strong>{component.label}:</strong> {component.value}
                                </div>
                              ))}
                            </div>
                          );
                        } else {
                          addressContent = <div style={{ marginLeft: '10px' }}>Not available</div>;
                        }
                      } else {
                        // Parsed, but not an array (should not happen with current save logic)
                        addressContent = <div style={{ marginLeft: '10px' }}>{businessDetails.address}</div>;
                      }
                    } catch (e) {
                      // Parsing failed, display as is (likely old format)
                      addressContent = <div style={{ marginLeft: '10px' }}>{businessDetails.address}</div>;
                    }
                  } else {
                    addressContent = <div style={{ marginLeft: '10px' }}>Not available</div>;
                  }
                  return addressContent;
                })()}
              </div>
              <p>
                <strong>Store Type:</strong> {businessDetails.storeType}
              </p>
              <p>
                <strong>Territory:</strong> {(() => {
                  // Try to get territory name by ID first
                  if (businessDetails.territoryId != null && !isNaN(Number(businessDetails.territoryId))) {
                    const territory = territories.find(t => t.id === Number(businessDetails.territoryId));
                    if (territory) return territory.name;
                  }
                  // Fall back to territory name field
                  if (businessDetails.territory) return businessDetails.territory;
                  return 'Not assigned';
                })()}
              </p>
              
            </CardBody>
            <CardFooter className="d-flex align-items-center justify-content-between" style={isArchived ? { opacity: 0.6 } : {}}>
              {businessDetails.warningMessage ? 
                <div className="d-flex align-items-center">
                  <i className="fa fa-exclamation-triangle mr-2"></i>
                  <span>{businessDetails.warningMessage}</span>
                </div>
                : <span className="text-muted">No Alerts</span>
              }
              <div>
                {businessDetails.directDebitActive ? (
                  <i
                    className="fa fa-check-circle text-success"
                    title="Active Direct Debit Approved"
                    style={{ fontSize: "1.5rem" }}
                  ></i>
                ) : (
                  <i
                    className="fa fa-times-circle text-danger"
                    title="No Active Direct Debit"
                    style={{ fontSize: "1.5rem" }}
                  ></i>
                )}
              </div>
            </CardFooter>
          </Card>

          {/* Connected Stores Card */}
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center" style={isArchived ? { opacity: 0.6 } : {}}>
              <CardTitle tag="h4" className="mb-0">Connected Stores</CardTitle>
            </CardHeader>
            <CardBody style={isArchived ? { opacity: 0.6 } : {}}>
              <FormGroup>
                <Label><strong>Selected</strong></Label>
                <div>
                  <ul className="nav nav-pills flex-wrap connected-stores-pills">
                    {selectedStoreOptions.length === 0 && (
                      <li className="text-muted">No connected stores</li>
                    )}
                    {selectedStoreOptions.map(opt => (
                      <li key={opt.value} className="nav-item mr-2 mb-2">
                        <a href="#" className="nav-link active d-flex align-items-center" onClick={(e) => e.preventDefault()}>
                          <span>{opt.label}</span>
                          {!isArchived && (
                            <button
                              type="button"
                              aria-label="Remove"
                              className="close ml-2"
                              onClick={(e) => { e.preventDefault(); removeConnectedStore(opt.value); }}
                              style={{ float: 'none', color: '#fff', opacity: 1, lineHeight: 1 }}
                            >
                              <span aria-hidden="true" style={{ color: '#fff' }}>×</span>
                            </button>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="addConnectedStore"><strong>Add Store</strong></Label>
                <Select
                  inputId="addConnectedStore"
                  isClearable
                  isDisabled={isArchived}
                  options={availableStoreOptions}
                  value={addStoreValue}
                  onChange={(opt) => addConnectedStore(opt)}
                  placeholder="Search and add a store…"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  components={{ IndicatorSeparator: () => null }}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 9999
                    })
                  }}
                  menuPortalTarget={document.body}
                />
              </FormGroup>
            </CardBody>
            <CardFooter className="d-flex justify-content-end" style={isArchived ? { opacity: 0.6 } : {}}>
              <Button color="secondary" onClick={handleCancelConnectedStores} disabled={isArchived} className="mr-2">
                Cancel
              </Button>
              <Button color="primary" onClick={handleSaveConnectedStores} disabled={isArchived}>
                Save
              </Button>
            </CardFooter>
          </Card>

          {/* Contact Details Card */}
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center" style={isArchived ? { opacity: 0.6 } : {}}>
              <CardTitle tag="h4" className="mb-0">Contact Details</CardTitle>
              <div>
              {customerContacts.length > 1 && (
                  <Button color="secondary" onClick={handleNextContact} disabled={isArchived}>
                    Next
                  </Button>
          )}
              </div>
            </CardHeader>
            <CardBody style={isArchived ? { opacity: 0.6 } : {}}>
              {customerContacts.length > 0 && currentContact ? (
                <div>
                  <div className="d-flex align-items-center mb-3">
                    <div>
                      <h5 className="mb-1" style={{ fontWeight: "bold" }}>
                        {currentContact.name}
                      </h5>
                      <p className="text-muted mb-0">{currentContact.title}</p>
                    </div>
                  </div>
                  <hr />
                  <p>
                    <strong>Email:</strong> {currentContact.email}
                  </p>
                  <hr />
                  <p>
                    <strong>Phone:</strong> {currentContact.phone}
                  </p>
                </div>
              ) : (
                <p>No contacts available.</p>
              )}
            </CardBody>
            <CardFooter style={isArchived ? { opacity: 0.6 } : {}}>
               <div className="d-flex justify-content-between">
                <Button 
                  color="primary" 
                  size="sm" 
                  className="btn-round btn-icon btn-icon-mini"
                  onClick={isArchived ? null : openAddContactModal}
                  disabled={isArchived}
                  title="Add New Contact"
                >
                  <i className="nc-icon nc-simple-add" />
                </Button>

              {customerContacts.length > 0 && currentContact && (
                <Button 
                  color="info" 
                  size="sm" 
                  className="btn-round btn-icon btn-icon-mini"
                  onClick={isArchived ? null : openEditContactModal}
                  disabled={isArchived}
                  title="Edit Contact"
                >
                  <i className="nc-icon nc-ruler-pencil" />
                </Button>
              )}
              </div>
            </CardFooter>
          </Card>
        </Col>

        <Col md="6">
        {applicablePromotions.length > 0 &&
          applicablePromotions.map(promo => (
            <Card key={promo.id} className="mb-3" style={isArchived ? { opacity: 0.6 } : {}}>
              <CardHeader>
                <CardTitle tag="h4">{promo.label || 'Special Offer'}</CardTitle>
              </CardHeader>
              <CardBody className="d-flex align-items-center">
              <img
                src={
                  // if pictureUrl is already a full S3 URL, just use it
                  promo.pictureUrl?.startsWith('http')
                    ? promo.pictureUrl
                    // otherwise build it
                    : s3ImageUrl(
                        "promotions",
                        promo.id,
                        // if you happen to store ext on the promo object, use it;
                        // otherwise default to .png
                        promo.ext ? `.${promo.ext}` : ".png"
                      )
                }
                alt={promo.label}
                style={{
                  maxWidth: "100px",
                  marginRight: "20px",
                  borderRadius: "8px"
                }}
                onError={e => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = placeholder;
                }}
              />
                <div>
                  <p className="mb-1" style={{ fontWeight: "bold" }}>
                    {promo.label}
                  </p>
                  <p className="mb-0">{promo.description}</p>
                </div>
              </CardBody>
            </Card>
          ))
        }
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center" style={isArchived ? { opacity: 0.6 } : {}}>
          <CardTitle tag="h4" className="mb-0">Order History</CardTitle>
      <FormGroup className="mb-0 mx-auto">
      {/* <Label for="orderDateRange"><strong>Date Range</strong></Label> */}
      <DatePicker
        id="orderDateRange"
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={(update) => setDateRange(update)}
        isClearable
        placeholderText="Select date range"
        className="form-control"
        disabled={isArchived}
      />
    </FormGroup>
          <Button color="primary" onClick={openAddOrderModal} disabled={isArchived}>
            Add New Order
          </Button>
        </CardHeader>

        <CardBody style={isArchived ? { opacity: 0.6 } : {}}>
        <Row form className="mb-3">

</Row>
            <div
              style={{
                maxHeight: (businessDetails.orderHistory || []).length > 5 ? "300px" : "auto",
                overflowY: (businessDetails.orderHistory || []).length > 5 ? "auto" : "visible",
                overflowX: "hidden"
              }}
            >
            <Table hover>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Order ID</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                  {filteredOrders.map(( order, idx) => {
                    // 1) look up this order’s distribution record
                    const dist = data.distributions.find(d => d.name === order.distribution);
                    const isPengelly = dist?.is_pengelly;

                    return (
                      <tr
                        key={idx}
                        style={{
                          cursor: isArchived ? 'default' : 'pointer',
                          backgroundColor: isArchived ? '#f8f9fa' : 'inherit'
                        }}
                        onClick={isArchived ? null : () => handleOrderClick(order)}
                      >
                        <td>
                          {order.date
                            ? new Date(order.date).toLocaleDateString('en-NZ', {
                                day:   '2-digit',
                                month: '2-digit',
                                year:  'numeric'
                              })
                            : '—'}
                        </td>
                        <td>{order.orderId}</td>
                        <td>${Number(order.total).toFixed(2)}</td>
                        <td>
                          {/*
                            Only for Pengelly distributions:
                            • red “Failed” if pick is null/undefined
                            • green “Success” otherwise
                            • plus a button that won’t bubble up to the row
                          */}
                          {isPengelly && (
                            <>
                              {order.pick != null
                                ? <Badge color="success">Success</Badge>
                                : <Badge color="danger">Failed</Badge>
                              }
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}


              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

          {/* Ranged Products Card */}
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center" style={isArchived ? { opacity: 0.6 } : {}}>
              <CardTitle tag="h4" className="mb-0">Ranged Products</CardTitle>
              <Badge color="info">{rangedProducts.length}</Badge>
            </CardHeader>
            <CardBody style={isArchived ? { opacity: 0.6 } : {}}>
              <FormGroup>
                <Label htmlFor="addRangedProduct"><strong>Add Product</strong></Label>
                <Select
                  inputId="addRangedProduct"
                  isClearable
                  isDisabled={isArchived}
                  options={availableRangeProductOptions}
                  value={addRangeProductValue}
                  onChange={(opt) => addRangedProduct(opt)}
                  placeholder="Search and add a product..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  components={{ IndicatorSeparator: () => null }}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 9999
                    })
                  }}
                  menuPortalTarget={document.body}
                />
              </FormGroup>

              <div style={{ maxHeight: 360, overflowY: rangedProducts.length > 5 ? "auto" : "visible" }}>
                {rangedProducts.length === 0 ? (
                  <p className="text-muted mb-0">No ranged products selected.</p>
                ) : (
                  rangedProducts.map(product => {
                    const brandName = getProductBrandName(product);
                    const imageUrl = product.id
                      ? `${s3ImageUrl("products", product.id, ".png")}?cb=${Date.now()}`
                      : placeholder;

                    return (
                      <div
                        key={product.id}
                        className="d-flex align-items-center py-2"
                        style={{ borderBottom: "1px solid #eee" }}
                      >
                        <img
                          src={imageUrl}
                          alt={product.name}
                          style={{
                            width: 44,
                            height: 44,
                            objectFit: "contain",
                            marginRight: 12,
                            background: "#fff",
                          }}
                          onError={e => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = placeholder;
                          }}
                        />
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div
                            title={product.name}
                            style={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {product.name}
                          </div>
                          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                            {[brandName, product.code].filter(Boolean).join(" - ")}
                          </div>
                        </div>
                        {!isArchived && (
                          <Button
                            color="link"
                            className="text-danger"
                            onClick={() => removeRangedProduct(product.id)}
                            title="Remove from range"
                          >
                            <i className="fa fa-times" />
                          </Button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </CardBody>
            <CardFooter className="d-flex justify-content-end" style={isArchived ? { opacity: 0.6 } : {}}>
              <Button
                color="secondary"
                onClick={handleCancelRangedProducts}
                disabled={isArchived || isSavingRangeProducts}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleSaveRangedProducts}
                disabled={isArchived || isSavingRangeProducts}
              >
                {isSavingRangeProducts ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </CardFooter>
          </Card>

        </Col>
      </Row>

      {/* Charts */}
      <Row>
        <Col md="6">
          <Card style={isArchived ? { opacity:  0.6 } : {}}>
            <CardHeader className="d-flex justify-content-between align-items-start">
              <CardTitle tag="h4" className="mb-0">Revenue by Account</CardTitle>
              <div className="text-right">
                <small className="text-muted d-block">Avg monthly spend</small>
                <strong>${Math.round(averageMonthlyRevenue).toLocaleString()}</strong>
              </div>
            </CardHeader>
            <CardBody>
              <Bar
                data={revenueChartData}
                options={revenueChartOptions}
                height={300}
              />
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card style={isArchived ? { opacity: 0.6 } : {}}>
            <CardHeader className="d-flex justify-content-between align-items-start">
              <CardTitle tag="h4" className="mb-0">Customer Orders</CardTitle>
              <div className="text-right">
                <small className="text-muted d-block">Avg monthly orders</small>
                <strong>{averageMonthlyOrders.toLocaleString(undefined, { maximumFractionDigits: 1 })}</strong>
              </div>
            </CardHeader>
            <CardBody>
              <Bar
                data={orderChartData}
                options={orderChartOptions}
                height={300}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
     {/* Top Brands Section */}
      <Row>
        <Col md="12">
          <Card style={isArchived ? { opacity: 0.6 } : {}}>
            <CardHeader>
              <CardTitle tag="h4">Top Brands</CardTitle>
            </CardHeader>
            <CardBody>
              <Row>
                {Array.isArray(businessDetails.topBrands) && businessDetails.topBrands.length > 0 ? (
                  businessDetails.topBrands.map(({ brandId, brandName, revenue }) => {
                    // Optional brand image (falls back to placeholder if missing)
                    const imgUrl = brandId
                      ? `${s3ImageUrl("brands", brandId, ".png")}?cb=${Date.now()}`
                      : placeholder;

                    return (
                      <Col key={brandId || brandName} xs="12" sm="6" md="4" lg="2" className="mb-3">
                        <Card className="h-100">
                          <div
                            style={{
                              height: 140,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              backgroundColor: '#fff'
                            }}
                          >
                            <img
                              src={imgUrl}
                              alt={brandName || `Brand ${brandId}`}
                              style={{ maxHeight: '100%', width: '100%', objectFit: 'contain' }}
                              onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = placeholder; }}
                            />
                          </div>
                          <CardBody>
                            <h5 className="card-title" title={brandName || `Brand ${brandId}`}>
                              {brandName || `Brand ${brandId}`}
                            </h5>
                            <p className="card-text">
                              <strong>Revenue:</strong>{' '}
                              ${Number(revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                          </CardBody>
                        </Card>
                      </Col>
                    );
                  })
                ) : (
                  <Col>
                    <p className="text-muted">No top-product data available.</p>
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* Top Products Section */}
      <Row>
        <Col md="12">
          <Card style={isArchived ? { opacity: 0.6 } : {}}>
            <CardHeader>
              <CardTitle tag="h4">Top Products Ordered</CardTitle>
            </CardHeader>
            <CardBody>
              <Row>
                {businessDetails.topProducts?.length > 0 ? (
                  businessDetails.topProducts.map(({ productId, revenue }) => {
                    // lookup the product record
                    const prod = products.find(p => p.id === productId) || {};
                    // build an S3 image URL with timestamp
                    const imgUrl = prod.id
                      ? `${s3ImageUrl("products", prod.id, ".png")}?cb=${Date.now()}`
                      : placeholder;

                    return (
                      <Col key={productId} xs="12" sm="6" md="4" lg="2" className="mb-3">
                        <Card className="h-100">
                          <img
                            src={imgUrl}
                            alt={prod.name || `#${productId}`}
                            className="card-img-top"
                            style={{ height: "500px", objectFit: "cover" }}
                            onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = placeholder; }}
                          />
                          <CardBody>
                            <h5 className="card-title">
                              {prod.name || `Product ${productId}`}
                            </h5>
                            <p className="card-text">
                              <strong>Revenue:</strong>{" "}
                              ${revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                          </CardBody>
                        </Card>
                      </Col>
                    );
                  })
                ) : (
                  <Col>
                    <p className="text-muted">No top-product data available.</p>
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

{/* Notes Section */}
<Row>
  <Col md="12">
    <Card>
      <CardHeader style={isArchived ? { opacity: 0.6 } : {}}>
        <CardTitle tag="h4">Notes</CardTitle>
      </CardHeader>
      <CardBody style={isArchived ? { opacity: 0.6 } : {}}>
        {displayedNotes.map((note) => (
          <div
            key={note.id}
            className="d-flex mb-3"
            style={{ borderBottom: "1px solid #eee", paddingBottom: 10 }}
          >
            <Avatar
              src={
                note.authorImage?.startsWith("http")
                  ? note.authorImage
                  : s3ImageUrl("users", note.authorId, ".png") + `?cb=${Date.now()}`
              }
              alt={note.authorName}
              size={40}
              className="me-3"
            />
            <div className="flex-grow-1" style={{ marginLeft: 16 }}>
              {/* header row: name/timestamp on left, edit button on right */}
              <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '4px' }}>
                <div>
                  <strong>{note.authorName}</strong>{" "}
                  <span className="text-muted" style={{ fontSize: "0.8em" }}>
                    {formatFullTimestamp(note.timestamp)}
                  </span>
                </div>
                {note.authorName === userProfile.name &&
                  editingNoteId !== note.id && (
                    <Button
                      color="link"
                      onClick={() => handleEditNote(note.id)}
                      disabled={isArchived}
                      style={{ padding: '0', margin: '0', height: 'auto' }}
                    >
                      Edit
                    </Button>
                  )}
              </div>
              {/* body: either textarea or content */}
              {editingNoteId === note.id ? (
                <>
                  <Input
                    type="textarea"
                    value={editedNoteContent}
                    onChange={(e) => setEditedNoteContent(e.target.value)}
                    disabled={isArchived}
                  />
                  <div className="mt-2">
                    <Button
                      color="primary"
                      onClick={() => handleSaveEdit(note.id)}
                      disabled={isArchived}
                    >
                      Save
                    </Button>{" "}
                    <Button
                      color="secondary"
                      onClick={handleCancelEdit}
                      disabled={isArchived}
                    >
                      Cancel
                    </Button>{" "}
                      <Button
                        color="danger"
                        onClick={() => handleDeleteNote(note.id)}
                        disabled={isArchived}
                      >
                        Delete
                      </Button>
                  </div>
                </>
              ) : (
                <div style={{ whiteSpace: 'pre-line' }}>{note.content?.trim()}</div>
              )}
            </div>
          </div>
        ))}

        {customerNotes.length > 5 && (
          <Button
            color="link"
            onClick={toggleNotesExpanded}
            disabled={isArchived}
          >
            {notesExpanded ? "Show Less" : "Show More"}
          </Button>
        )}

        <div className="mt-4">
          <Input
            type="textarea"
            placeholder={
              isArchived
                ? "Cannot add notes to an archived customer"
                : "Add a new note..."
            }
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            disabled={isArchived}
            style={{ padding: '10px', lineHeight: '1.5' }}
          />
          <Button
            className="mt-2"
            color="primary"
            onClick={handleAddNote}
            disabled={isArchived}
          >
            Add Note
          </Button>
        </div>
      </CardBody>
    </Card>
  </Col>
</Row>




{/* Order Details Modal */}
{selectedOrder && (
  <Modal
    isOpen={orderModalOpen}
    toggle={toggleOrderModal}
    size="lg"
    centered
  >
    <ModalHeader toggle={toggleOrderModal}>
      {businessDetails.businessName}<br/>
      Order #: {selectedOrder.orderId}
    </ModalHeader>
<ModalBody>
  {loadingOrder ? (
    <div className="text-center my-5">
      <i className="fa fa-spinner fa-spin fa-2x" />
    </div>
  ) : orderDetails ? (
    <>
      {/* ─── Order Meta ────────────────────────────────────────────── */}
      <Row className="mb-3">
        <Col md="2">
          <strong>Date:</strong><br/>
            {orderDetails.date
                ? new Date(orderDetails.date).toLocaleDateString('en-NZ', {
                    day:   '2-digit',
                    month: '2-digit',
                    year:  'numeric'
                  })
                : '—'}
        </Col>
        <Col md="2">
          <strong>Store #:</strong><br/>
          {orderDetails.storeNumber || "–"}
        </Col>
        <Col md="2">
          <strong>PO #:</strong><br/>
          {orderDetails.purchaseOrder || "–"}
        </Col>
        <Col md="2">
          <strong>Distribution:</strong><br/>
          {orderDetails.distribution || "–"}
        </Col>
        <Col md="2">
          <strong>Pengelly #:</strong><br/>
          {orderDetails.pick
            ? orderDetails.pick
            : (
              <Button
                size="sm"
                color="warning"
                disabled={retrying || !isSuperuser}
                onClick={e => {
                  e.stopPropagation();
                  retryPengelly();
                }}
              >
                {retrying ? "Retrying…" : "Retry"}
              </Button>
            )
          }
        </Col>
      </Row>

      {/* ─── Order-level Notes ───────────────────────────────────────── */}
      {orderDetails.notes && (
        <Row className="mb-3">
          <Col>
            <strong>Notes:</strong>
            <div className="p-2 border rounded" style={{ whiteSpace: "pre-wrap" }}>
              {orderDetails.notes}
            </div>
          </Col>
        </Row>
      )}

      {/* ─── Line Items ─────────────────────────────────────────────── */}
      {(orderDetails.updatedAt || orderDetails.updatedByEmail) && (
        <div className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
          Updated: {orderDetails.updatedAt ? formatFullTimestamp(orderDetails.updatedAt) : "—"}
          {" "}by {orderDetails.updatedByEmail || "—"}
        </div>
      )}
      <h5 className="mb-2">Products Ordered</h5>
      <div
        style={{
          maxHeight: "300px",
          overflowY: "auto",
          overflowX: "hidden",
          marginBottom: "1rem"
        }}
      >
        <Table hover size="sm">
          <thead className="thead-light">
            {(() => {
              const items = getNormalizedItems(orderDetails);
              const hasCustom = items.some(i => i.customPrice != null);
              return (
                <tr>
                  <th>Product</th>
                  <th className="text-right">Qty</th>
                  {hasCustom ? (
                    <>
                      <th className="text-right">Price</th>
                      <th className="text-right">Custom</th>
                    </>
                  ) : (
                    <th className="text-right">Price</th>
                  )}
                  <th className="text-right">Total</th>
                </tr>
              );
            })()}
          </thead>
          <tbody>
            {(() => {
              const items = getNormalizedItems(orderDetails);
              const hasCustom = items.some(i => i.customPrice != null);
              return items.map((item, i) => (
                <tr key={i}>
                  <td>{item.name}</td>
                  <td className="text-right">
                    {item.quantity}
                    {item.bonusQuantity ? ` (+${item.bonusQuantity})` : ""}
                  </td>
                  {hasCustom ? (
                    <>
                      <td className="text-right">${item.basePrice.toFixed(2)}</td>
                      <td className="text-right">{item.customPrice != null ? `$${item.customPrice.toFixed(2)}` : '—'}</td>
                    </>
                  ) : (
                    <td className="text-right">${item.price.toFixed(2)}</td>
                  )}
                  <td className="text-right">${item.total.toFixed(2)}</td>
                </tr>
              ));
            })()}
          </tbody>
        </Table>
      </div>

      {/* ─── Order Total + Delivery Flag ───────────────────────────── */}
      <Row className="align-items-center">
        {orderDetails?.delivery && (
          <Col className="text-left">
            <h6 className="mb-0">
              <i className="fa fa-truck mr-1" /> Delivery
            </h6>
          </Col>
        )}
        <Col className="text-right">
          <h5>
            Grand Total: $
            {(() => {
              const items = getNormalizedItems(orderDetails);
              const hasDeliveryItem = items.some(i => (i.productId === 'delivery_fee') || ((i.name || '').toLowerCase().includes('delivery')));
              const fee = (orderDetails?.delivery && !hasDeliveryItem) ? 25 : 0;
              const total = items.reduce((sum, i) => sum + i.total, 0) + fee;
              return total.toFixed(2);
            })()}
          </h5>
        </Col>
      </Row>
    </>
  ) : (
    <p className="text-center text-danger">
      Unable to load order details.
    </p>
  )}
</ModalBody>

    

    <ModalFooter className="d-flex justify-content-between px-4 py-2">
      <div className="d-flex">
        {canEditOrderEntries && (
          <Button
            color="warning"
            onClick={handleEditClick}
            disabled={loadingOrder || !orderDetails}
            style={{ marginRight: "0.5rem" }}
          >
            Edit
          </Button>
        )}
        <Button color="danger" onClick={handleDeleteClick}>
          Delete
        </Button>
      </div>
      <Button color="secondary" onClick={toggleOrderModal}>
        Close
      </Button>
    </ModalFooter>
  </Modal>
)}
<Modal
  isOpen={confirmEditOpen}
  toggle={handleCancelEditOrder}
  centered
>
  <ModalHeader toggle={handleCancelEditOrder}>
    Confirm Edit
  </ModalHeader>
  <ModalBody>
    This can not be undone. Are you sure you want to edit this order?
  </ModalBody>
  <ModalFooter className="px-4 py-2">
    <Button color="secondary" onClick={handleCancelEditOrder}>
      Cancel
    </Button>
    <Button color="warning" onClick={handleConfirmEditOrder}>
      Yes, Edit Order
    </Button>
  </ModalFooter>
</Modal>
<Modal
  isOpen={confirmDeleteOpen}
  toggle={handleCancelDelete}
  centered
  // Do not disable this modal if archived, as deleting an order might still be permissible
  // or handled by backend logic. If not, add: backdrop={isArchived ? "static" : true} keyboard={!isArchived}
>
  <ModalHeader toggle={handleCancelDelete}>
    Confirm Delete
  </ModalHeader>
  <ModalBody>
    Are you sure you want to delete this order? This can not be undone. 
  </ModalBody>
  <ModalFooter className="px-4 py-2">
    <Button color="secondary" onClick={handleCancelDelete}>
      Cancel
    </Button>
    <Button
      color="danger"
      onClick={handleConfirmDelete}
      disabled={deleting}
    >
      {deleting ? "Deleting…" : "Delete"}
    </Button>
  </ModalFooter>
</Modal>

      {/* Contact Modal for Editing/Adding */}
      {editingContact && (
        <Modal 
          isOpen={contactModalOpen} 
          toggle={handleCancelContactEdit}
          // Prevent interaction if archived, though opening it is already blocked
          backdrop={isArchived ? "static" : true}
          keyboard={!isArchived}
        >
          <ModalHeader toggle={handleCancelContactEdit}>
            {editingContact.id ? "Edit Contact" : "Add New Contact"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="contactName">Name</Label>
              <Input
                type="text"
                id="contactName"
                invalid={invalidName}
                value={editingContact.name}
                onChange={(e) =>
                  setEditingContact({ ...editingContact, name: e.target.value })
                }
              />
          {invalidName && (
            <small className="text-danger">Name is required</small>
          )}
            </FormGroup>
            <FormGroup>
              <Label for="contactEmail">Email</Label>
              <Input
                type="email"
                id="contactEmail"
                value={editingContact.email}
                onChange={(e) =>
                  setEditingContact({ ...editingContact, email: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="contactPhone">Phone</Label>
              <Input
                type="text"
                id="contactPhone"
                value={editingContact.phone}
                onChange={(e) =>
                  setEditingContact({ ...editingContact, phone: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="contactTitle">Title</Label>
              <Input
                type="text"
                id="contactTitle"
                value={editingContact.title}
                onChange={(e) =>
                  setEditingContact({ ...editingContact, title: e.target.value })
                }
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter style={{ padding: '0.75rem 1rem' }}>
            {editingContact && editingContact.id && (
              <Button 
                color="danger" 
                onClick={handleDeleteContactClick}
                disabled={isArchived}
                style={{ marginRight: 'auto' }}
              >
                Delete
              </Button>
            )}
            <Button color="primary" onClick={handleSaveContact}>
              Save
            </Button>
            <Button color="secondary" onClick={handleCancelContactEdit}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Contact Delete Confirmation Modal */}
      <Modal 
        isOpen={confirmDeleteContactOpen} 
        toggle={handleCancelDeleteContact} 
        centered
      >
        <ModalHeader toggle={handleCancelDeleteContact}>
          Confirm Delete Contact
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete the contact "{editingContact?.name}"? This action cannot be undone.
        </ModalBody>
        <ModalFooter className="px-4 py-2">
          <Button color="secondary" onClick={handleCancelDeleteContact}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleConfirmDeleteContact}
            disabled={deletingContact}
          >
            {deletingContact ? "Deleting…" : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>

{/* Business Details Editing Modal */}
{editingBusiness && (
  <Modal 
    isOpen={businessModalOpen} 
    toggle={handleCancelBusinessEdit}
    // This modal MUST remain interactive to allow unarchiving.
  >
    <ModalHeader toggle={handleCancelBusinessEdit}>
      {editingBusiness.id ? "Edit Business Details" : "Add New Customer"}
    </ModalHeader>
    <ModalBody>
    <Row>
        <Col md={8}>
    <FormGroup>
  <Label for="businessName">Business Name</Label>
      <Input
        type="text"
        id="businessName"
        value={editingBusiness.businessName}
        onChange={(e) => {
          setEditingBusiness({
            ...editingBusiness,
            businessName: e.target.value
          });
          if (validationErrors.businessName) {
            setValidationErrors(prev => ({...prev, businessName: null}));
          }
        }}
        invalid={!!validationErrors.businessName}
      />
      {validationErrors.businessName && (
        <small className="text-danger">{validationErrors.businessName}</small>
      )}
    </FormGroup>
    </Col>
    <Col md={4}>
          <FormGroup>
        <Label for="storeNumber">Store #</Label>
        <Input
          type="text"
          id="storeNumber"
          value={editingBusiness.storeNumber}
          onChange={(e) =>
            setEditingBusiness({
              ...editingBusiness,
              storeNumber: e.target.value
            })
          }
        />
      </FormGroup>
      </Col>
      </Row>
            <Row>
        <Col md={6}>
      <FormGroup>
        <Label for="storeType">Store Type</Label>
        <Input
          type="select"
          id="storeType"
          value={editingBusiness.storeType}
          onChange={(e) => {
            setEditingBusiness({
              ...editingBusiness,
              storeType: e.target.value
            });
            if (validationErrors.storeType) {
              setValidationErrors(prev => ({...prev, storeType: null}));
            }
          }}
        >
          <option value="">Select Store Type</option>
          {storeTypeOptions.filter(type => !type.is_archived).map((type, idx) => (
            <option key={idx} value={type.name}>{type.name}</option>
          ))}
        </Input>
        {validationErrors.storeType && (
  <small className="text-danger">{validationErrors.storeType}</small>
)}
      </FormGroup>
      </Col>
      <Col md={6}>
      <FormGroup>
        <Label for="territory">Territory</Label>
        <Input
          type="select"
          id="territory"
          value={selectedTerritoryId === null ? "" : selectedTerritoryId}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedTerritoryId(value === "" ? null : Number(value));
            if (validationErrors.territory) {
              setValidationErrors(prevErrors => ({
                ...prevErrors,
                territory: null
              }));
            }
          }}
          invalid={!!validationErrors.territory}
        >
          <option value="">Select Territory</option>
          {territories
            .filter(t => !t.is_archived)
            .sort((a, b) => a.id - b.id) // Sort by ID in ascending order
            .map((territory) => (
              <option key={territory.id} value={territory.id}>
                {territory.name}
              </option>
            ))}
        </Input>
        {validationErrors.territory && (
          <small className="text-danger">{validationErrors.territory}</small>
        )}
      </FormGroup>
      </Col>
      </Row>
      <Row>
        <Col md={6}>
      <FormGroup>
        <Label for="banner">Banner</Label>
        <Input
          type="select"
          id="banner"
          value={editingBusiness.banner}
          onChange={(e) => {
            setEditingBusiness({
              ...editingBusiness,
              banner: e.target.value
            });
            if (validationErrors.banner) {
              setValidationErrors(prev => ({...prev, banner: null}));
            }
          }}
        >
          <option value="">Select Banner</option>
          {bannerOptions.filter(banner => !banner.is_archived).map((banner, idx) => (
            <option key={idx} value={banner.name}>{banner.name}</option>
          ))}
        </Input>
                  {validationErrors.banner && (
            <small className="text-danger">{validationErrors.banner}</small>
          )}
      </FormGroup>
      </Col>
      <Col md={6}>
      </Col>
      </Row>
      <Row>
        <Col md={6}>
     <FormGroup>
        <Label for="contactEmail">Email</Label>
        <Input
          type="email"
          id="contactEmail"
          value={editingBusiness.contactEmail}
          onChange={(e) =>
            setEditingBusiness({
              ...editingBusiness,
              contactEmail: e.target.value
            })
          }
        />
      </FormGroup>
      </Col>
      <Col md={6}>
      <FormGroup>
        <Label for="phone">Phone</Label>
        <Input
          type="text"
          id="phone"
          value={editingBusiness.phone}
          onChange={(e) =>
            setEditingBusiness({
              ...editingBusiness,
              phone: e.target.value
            })
          }
        />
      </FormGroup>
      </Col>





      </Row>
 
      <Row>
        <Col md="12">
          <FormGroup>
            <Label for="street">Street Address</Label>
            <Input
              type="text"
              id="street"
              value={editingBusiness?.street || ""}
              onChange={(e) => handleEditingBusinessInputChange('street', e.target.value)}
            />
            {validationErrors.street && (
              <small className="text-danger">{validationErrors.street}</small>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="suburb">Suburb</Label>
            <Input
              type="text"
              id="suburb"
              value={editingBusiness?.suburb || ""}
              onChange={(e) => handleEditingBusinessInputChange('suburb', e.target.value)}
            />
            {/* Assuming suburb is optional, so no validation shown unless specified */}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="city">City/Town</Label>
            <Input
              type="text"
              id="city"
              value={editingBusiness?.city || ""}
              onChange={(e) => handleEditingBusinessInputChange('city', e.target.value)}
            />
            {validationErrors.city && (
              <small className="text-danger">{validationErrors.city}</small>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="postCode">Post Code</Label>
            <Input
              type="text"
              id="postCode"
              value={editingBusiness?.postCode || ""}
              onChange={(e) => handleEditingBusinessInputChange('postCode', e.target.value)}
              invalid={!!validationErrors.postCode}
            />
            {validationErrors.postCode && (
              <small className="text-danger">{validationErrors.postCode}</small>
            )}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="country">Country</Label>
            <Input
              type="text"
              id="country"
              value={editingBusiness?.country || "New Zealand"}
              disabled
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="distribution">Distribution</Label>
            <Input
              type="select"
              id="distribution"
              value={editingBusiness?.distribution || ""}
              onChange={(e) => handleEditingBusinessInputChange('distribution', e.target.value)}
              invalid={!!validationErrors.distribution}
            >
              <option value="">Select Distribution</option>
              {distributionsFromContext
                .filter(dist => !dist.is_archived) // Assuming is_archived field
                .map((dist) => (
                  <option key={dist.id} value={dist.name}>
                    {dist.name}
                  </option>
                ))}
            </Input>
            {validationErrors.distribution && (
              <small className="text-danger">{validationErrors.distribution}</small>
            )}
          </FormGroup>
        </Col>
        <Col md="6">
        </Col>
      </Row>
      {/* <Row>
        <Col md="12">
          <FormGroup>
            <Label for="connectedStores">Connected Stores</Label>
            <Select
              isMulti
              id="connectedStores"
              options={customerStoreOptions}
              value={customerStoreOptions.filter(opt => (connectedStoresSelection || []).includes(opt.value))}
              onChange={(selectedOptions) => {
                const storeIds = (selectedOptions || []).map(option => option.value);
                handleEditingBusinessInputChange('connectedStores', storeIds);
              }}
              placeholder="Select connected stores..."
              className="react-select-container"
              classNamePrefix="react-select"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ ValueContainer: ConnectedStoresValueContainer, IndicatorSeparator: () => null }}
              isDisabled={isArchived}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 9999
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999
                })
              }}
              menuPortalTarget={document.body}
            />
          </FormGroup>
        </Col>
      </Row> */}
      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Direct Debit</Label>
            <div className="d-flex align-items-center">
              <div className="custom-control custom-switch mr-2">
                <Input
                  type="checkbox"
                  id="directDebitActive"
                  className="custom-control-input"
                  checked={!!editingBusiness?.directDebitActive}
                  onChange={(e) => handleEditingBusinessInputChange('directDebitActive', e.target.checked)}
                />
                <Label className="custom-control-label" htmlFor="directDebitActive"></Label>
              </div>
              <span className="mb-0">
                {editingBusiness?.directDebitActive ? "Active" : "Inactive"}
              </span>
            </div>
          </FormGroup>
        </Col>
        <Col md="6">
        </Col>
      </Row>
      <Row>
        <Col md="12">
          <FormGroup>
            <Label for="warningMessage">Account Alert</Label>
            <Input
              type="textarea"
              id="warningMessage"
              rows="2"
              value={editingBusiness?.warningMessage || ''}
              onChange={e => handleEditingBusinessInputChange('warningMessage', e.target.value)}
            />
          </FormGroup>
        </Col>
      </Row>
    </ModalBody>
    <ModalFooter style={{ padding: '0.75rem 1rem' }}>
      {editingBusiness && editingBusiness.id && ( // Show only when editing an existing customer
        <Button
          color={editingBusiness.archived ? "success" : "danger"}
          style={{
            backgroundColor: editingBusiness.archived ? 'lightgreen' : 'lightcoral',
            marginRight: 'auto'
          }}
          onClick={openArchiveCustomerConfirmModal}
        >
          {editingBusiness.archived ? "Unarchive" : "Archive"}
        </Button>
      )}
      <Button 
        color="primary" 
        onClick={handleSaveBusinessEdit} 
        disabled={isSavingCustomer || !!validationErrors.businessName || !!validationErrors.storeType || !!validationErrors.banner || !!validationErrors.territory || !!validationErrors.distribution || !!validationErrors.street || !!validationErrors.city || !!validationErrors.postCode}
      >
        {isSavingCustomer ? (
          <><i className="fa fa-spinner fa-spin" /> Saving...</>
        ) : editingBusiness && editingBusiness.id ? (
          "Save Changes"
        ) : (
          "Add Customer"
        )}
      </Button>
      <Button color="secondary" onClick={handleCancelBusinessEdit} disabled={isSavingCustomer}>
        Cancel
      </Button>
    </ModalFooter>
  </Modal>
)}

      {/* Customer Archive Confirmation Modal */}
      <Modal isOpen={archiveCustomerConfirmModalOpen} toggle={closeArchiveCustomerConfirmModal} centered>
        <ModalHeader toggle={closeArchiveCustomerConfirmModal}>
          Confirm {editingBusiness?.archived ? "Unarchive" : "Archive"} Customer
        </ModalHeader>
        <ModalBody>
          Are you sure you want to {editingBusiness?.archived ? "unarchive" : "archive"} the customer "{editingBusiness?.businessName}"?
        </ModalBody>
        <ModalFooter style={{ padding: '0.75rem 1rem' }}>
          <Button color={editingBusiness?.archived ? "success" : "danger"} onClick={handleConfirmCustomerArchive}>
            Yes, {editingBusiness?.archived ? "Unarchive" : "Archive"}
          </Button>
          <Button color="secondary" onClick={closeArchiveCustomerConfirmModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add New Order Modal (Reusable Component) */}
      <AddNewOrderModal
        isOpen={addOrderModalOpen}
        toggle={toggleAddOrderModal}
        onSave={handleAddOrderSave}
        defaultCustomer={selectedCustomerId}
        // Pass isArchived to AddNewOrderModal if it needs to behave differently
        // For now, the button to open it is disabled, which is the primary control.
      />
      <AddNewOrderModal
        isOpen={editOrderModalOpen}
        toggle={toggleEditOrderModal}
        onSave={handleAddOrderSave}
        defaultCustomer={selectedCustomerId}
        existingOrder={orderBeingEdited}
      />

    </div>
  );
}

export default CustomerDashboard;
